import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ToastService } from '@atsystems/ui';
import { filter, Subscription, take } from 'rxjs';
import {
  FEEDBACK_GOTO_ATTEMPT_SEPARATOR, FEEDBACK_GOTO_QUERY_PARAM, FEEDBACK_GOTO_STORAGE_PREFIX, FEEDBACK_GOTO_TTL_MS,
} from '../models/feedback.model';
import { feedbackToast } from '../helpers/feedback-toast';

@Injectable({ providedIn: 'root' })
export class FeedbackGotoService {
  private readonly doc = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private initialized = false;
  private stopLocator?: () => void;
  private leaveWatch?: Subscription;
  /** Bumped when a search is cancelled: late callbacks of an older search are ignored. */
  private generation = 0;
  private storage: Storage | null = null;
  private session: Storage | null = null;
  private handoff: FeedbackHandoff | null = null;

  public initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    const win = this.doc.defaultView;
    if (!win) return;
    let storage: Storage | null = null;
    let session: Storage | null = null;
    try { storage = win.localStorage; } catch { /* Browser storage may be disabled. */ }
    try { session = win.sessionStorage ?? null; } catch { /* Browser storage may be disabled. */ }
    const href = win.location.href;
    const handoff = captureFeedbackHandoff(href, storage, Date.now(), session);
    const target = handoff.target;
    if (!target.requested) return;
    this.storage = storage;
    this.session = session;
    this.handoff = handoff;
    this.destroyRef.onDestroy(() => this.cancel());
    if (this.router.navigated) {
      this.start(target);
    } else {
      this.router.events.pipe(filter(event => event instanceof NavigationEnd), take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.start(target));
    }
  }

  private start(target: FeedbackGotoTarget): void {
    this.releaseHandoff();
    if (!target.selector) {
      this.complete(false);
      return;
    }
    const selector = target.selector;
    const generation = ++this.generation;
    // The search belongs to the page the link was opened for. Filters rewriting the query (and our own
    // fb cleanup via replaceState) stay on that page; a navigation to another path cancels the search,
    // so a matching selector is never spotlit on a page the user moved to.
    const page = pagePath(this.router.url);
    this.leaveWatch = this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart && pagePath(event.url) !== page),
      take(1),
    ).subscribe(() => this.cancel());
    const current = (): boolean => !this.destroyRef.destroyed && generation === this.generation;
    this.loadLocator().then(({ locateFeedbackElement }) => {
      if (!current()) return;
      this.zone.runOutsideAngular(() => {
        this.stopLocator = locateFeedbackElement(this.doc, selector, found => this.zone.run(() => {
          if (current()) this.complete(found);
        }));
      });
    }).catch(() => { if (current()) this.complete(false); });
  }

  private loadLocator(): Promise<typeof import('../../pages/feedback/utils/feedback-locator')> {
    return import('../../pages/feedback/utils/feedback-locator');
  }

  /** Stops the observer, the 15 s timer and the spotlight, and invalidates a pending locator import. */
  private cancel(): void {
    this.generation++;
    this.leaveWatch?.unsubscribe();
    this.leaveWatch = undefined;
    const stop = this.stopLocator;
    this.stopLocator = undefined;
    stop?.();
  }

  private complete(found: boolean): void {
    const win = this.doc.defaultView;
    if (win) {
      const url = new URL(win.location.href);
      url.searchParams.delete(FEEDBACK_GOTO_QUERY_PARAM);
      win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
    }
    if (!found) this.toast.add(feedbackToast('warning', 'Element not found'));
  }

  // Keycloak login-required reloads the document before the first NavigationEnd, and the document that
  // comes back from the login redirect has to get the same target. The attempt this tab took lives in its
  // sessionStorage until the first navigation (it already left localStorage, so no other tab can take it).
  // Without sessionStorage the attempt stays in localStorage until then, and only that exact entry is removed.
  // The 60 s TTL cleans up anything left behind.
  private releaseHandoff(): void {
    const handoff = this.handoff;
    this.handoff = null;
    if (!handoff?.key) return;
    try { this.session?.removeItem(handoff.key); } catch { /* Storage can be blocked. */ }
    try {
      const attempt = handoff.attempt;
      if (attempt && handoff.raw !== null && this.storage?.getItem(attempt) === handoff.raw) this.storage.removeItem(attempt);
    } catch { /* Storage can be blocked. */ }
  }
}


export interface FeedbackGotoTarget {
  requested: boolean;
  selector: string | null;
}

/** The target plus the attempt this tab took, so it can release only that attempt. */
export interface FeedbackHandoff {
  target: FeedbackGotoTarget;
  /** `feedback-goto:<id>`: the key of this tab's copy in sessionStorage. */
  key: string | null;
  /** localStorage key of the attempt this tab took. */
  attempt: string | null;
  /** The exact record this tab took. */
  raw: string | null;
}

interface HandoffRecord {
  selector: string;
  ts: number;
  nonce?: unknown;
}

interface HandoffAttempt {
  key: string;
  raw: string;
  record: HandoffRecord;
}

/** Router URL without query and fragment: identifies the page itself. */
function pagePath(url: string | undefined): string {
  return (url ?? '').split(/[?#]/)[0];
}

function readRecord(raw: string | null, now: number): HandoffRecord | null {
  try {
    const record = JSON.parse(raw ?? 'null');
    if (typeof record?.selector !== 'string' || !record.selector.trim() || !Number.isFinite(record.ts)) return null;
    const age = now - record.ts;
    return age >= 0 && age <= FEEDBACK_GOTO_TTL_MS ? record : null;
  } catch {
    return null;
  }
}

function readSelector(raw: string | null, now: number): string | null {
  return readRecord(raw, now)?.selector ?? null;
}

/** localStorage key a record was written under: `<base>:<nonce>` per click, or `<base>` from earlier versions. */
function attemptKey(base: string, record: HandoffRecord): string {
  return typeof record.nonce === 'string' && record.nonce ? base + FEEDBACK_GOTO_ATTEMPT_SEPARATOR + record.nonce : base;
}

/** The earliest valid attempt for the ticket: per-click entries `<base>:<nonce>` and the single `<base>` record of
 * earlier versions. An invalid `<base>` record is removed; invalid per-click entries go with the TTL sweep. */
function earliestAttempt(storage: Storage, base: string, now: number): HandoffAttempt | null {
  const attempts: HandoffAttempt[] = [];
  const legacy = storage.getItem(base);
  const legacyRecord = readRecord(legacy, now);
  if (legacyRecord) attempts.push({ key: base, raw: legacy!, record: legacyRecord });
  else storage.removeItem(base);
  const prefix = base + FEEDBACK_GOTO_ATTEMPT_SEPARATOR;
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (!key?.startsWith(prefix)) continue;
    const raw = storage.getItem(key);
    const record = readRecord(raw, now);
    if (record) attempts.push({ key, raw: raw!, record });
  }
  attempts.sort((a, b) => a.record.ts - b.record.ts || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return attempts[0] ?? null;
}

/** Keeps the attempt in this tab's sessionStorage; true only if the copy is really there. */
function keepInTab(session: Storage | null, key: string, raw: string): boolean {
  if (!session) return false;
  try {
    session.setItem(key, raw);
    return session.getItem(key) === raw;
  } catch {
    return false;
  }
}

export function captureFeedbackTarget(href: string, storage: Storage | null, now: number): FeedbackGotoTarget {
  return captureFeedbackHandoff(href, storage, now).target;
}

/**
 * Reads the handoff for the fb in the URL. Every Go to element click leaves its own attempt in localStorage,
 * and the URL carries only the ticket id, so a new tab takes ONE attempt (the earliest): it copies it into its
 * own sessionStorage (`session`) and removes it from localStorage at once, leaving the others to the tabs of the
 * other clicks. The document that returns from the Keycloak redirect reads the tab copy, never a second attempt.
 * Without sessionStorage the attempt stays in localStorage until FeedbackGotoService releases it after the first
 * navigation, so the Keycloak return can still read it.
 */
export function captureFeedbackHandoff(href: string, storage: Storage | null, now: number,
  session: Storage | null = null): FeedbackHandoff {
  const id = new URL(href).searchParams.get(FEEDBACK_GOTO_QUERY_PARAM);
  const key = id !== null ? FEEDBACK_GOTO_STORAGE_PREFIX + id : null;
  let selector: string | null = null;
  let raw: string | null = null;
  let attempt: string | null = null;
  // A copy in this tab's sessionStorage means this tab has already taken its attempt (the document is back from
  // the Keycloak redirect): it never takes a second one, even when the copy has expired, so it can't leave the tab
  // of another click without a target. The copy is removed on release.
  let taken = false;
  if (key && session) {
    try {
      const own = session.getItem(key);
      taken = own !== null;
      const record = readRecord(own, now);
      if (record) {
        selector = record.selector;
        raw = own;
        attempt = attemptKey(key, record);
      }
    } catch {
      selector = null;
    }
  }
  try {
    if (key && storage && !taken) {
      const earliest = earliestAttempt(storage, key, now);
      if (earliest) {
        selector = earliest.record.selector;
        raw = earliest.raw;
        attempt = earliest.key;
        if (keepInTab(session, key, earliest.raw)) storage.removeItem(earliest.key);
      }
    }
    if (storage) {
      for (let index = storage.length - 1; index >= 0; index--) {
        const name = storage.key(index);
        if (name?.startsWith(FEEDBACK_GOTO_STORAGE_PREFIX) && !readSelector(storage.getItem(name), now)) {
          storage.removeItem(name);
        }
      }
    }
  } catch {
    // Storage can be blocked by browser privacy settings.
  }
  return { target: { requested: id !== null, selector }, key, attempt, raw };
}
